'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  AlertTriangle,
  BarChart3,
  CheckCircle,
  DollarSign,
  FileText,
  Network,
  Package,
  RefreshCw,
  TrendingDown,
} from 'lucide-react';

import { DEFAULT_CURRENCY, formatMoney } from '@kit/shared/currency';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { CurrencyPicker } from '../_components/currency-picker';
import { RelatedTools } from '../_components/related-tools';
import type { MoqResult } from './_lib/calculate-moq';
import {
  calculateMoq,
  getMoqInputError,
  parseNumericInput,
} from './_lib/calculate-moq';

const baseUrl = appConfig.url;
const siteName = appConfig.name;
const calculatorUrl = `${baseUrl}/tools/moq-calculator`;
const toolsUrl = `${baseUrl}/tools`;

type FormData = {
  annualDemand: string;
  holdingCostPct: string;
  orderingCost: string;
  supplierMoq: string;
  unitCost: string;
};

const heroHighlights = [
  'Economic order quantity in one click',
  'Supplier MOQ compared against it',
  'The annual cost of the MOQ, priced',
] as const;

const optimizationSteps = [
  {
    title: '1. Ordering cost',
    description:
      'Every PO carries a real cost in freight coordination, customs, admin time, and inspection. Ordering too often makes that cost compound.',
    icon: FileText,
  },
  {
    title: '2. Holding cost',
    description:
      'Buying too much pushes cash into inventory and increases storage, insurance, and obsolescence risk over the time it sits.',
    icon: DollarSign,
  },
  {
    title: '3. Supplier MOQ reality',
    description:
      'The economic answer still has to clear the supplier minimum. When it does not, this page prices exactly what that minimum costs you per year.',
    icon: Package,
  },
] as const;

const whyItMatters = [
  {
    title: 'Stop over-ordering',
    description:
      'A clean EOQ view shows when supplier minimums are pushing inventory well above the economic optimum.',
  },
  {
    title: 'Protect working capital',
    description:
      'Right-sized purchase quantities leave more cash available for freight, marketing, and the next order cycle.',
  },
  {
    title: 'Make supplier tradeoffs visible',
    description:
      'When MOQ exceeds EOQ, you can quantify the annual premium and take that number into the negotiation.',
  },
] as const;

const faqItems = [
  {
    value: 'free',
    question: 'Is this MOQ and EOQ calculator free?',
    answer:
      'Yes. This free MOQ calculator runs entirely in your browser with no signup, no usage limit, and no credit card. Nothing you enter is sent to a server, and results update as you type.',
  },
  {
    value: 'eoq',
    question: 'What is the EOQ formula?',
    answer:
      'Economic Order Quantity is the square root of (2 × annual demand × cost per order) divided by the annual holding cost per unit. It is the order size where ordering cost and holding cost balance, which is where total inventory cost is at its lowest.',
  },
  {
    value: 'supplier-moq',
    question: 'What if my supplier MOQ is higher than my EOQ?',
    answer:
      'Then the minimum is forcing you above the economic optimum. This page prices the difference as the full annual cost gap between ordering at the MOQ and ordering at the EOQ, which nets off the ordering cost you save by ordering less often.',
  },
  {
    value: 'moq-premium',
    question: 'How is the MOQ premium calculated?',
    answer:
      'It is the total annual cost at the supplier minimum minus the total annual cost at the EOQ. Both totals are ordering cost plus holding cost. Charging only the extra holding cost, as many calculators do, overstates the premium because it ignores the orders you no longer place.',
  },
  {
    value: 'ordering-cost',
    question: 'What should go into cost per order?',
    answer:
      'Use the all-in cost of placing and receiving one order: freight coordination, customs handling, admin effort, inspection, and any fixed supplier charges. It should not include the price of the goods themselves.',
  },
  {
    value: 'holding-cost',
    question: 'How should I think about holding cost percentage?',
    answer:
      'It is the annual cost of keeping one unit in stock, as a percentage of unit cost: capital, storage, insurance, shrinkage, and obsolescence. Most importers land between 20% and 30%. It cannot be zero, because it is what stops the EOQ from being infinite.',
  },
  {
    value: 'purchase-cost',
    question: 'Does the annual cost include the goods?',
    answer:
      'No. The annual cost shown is ordering plus holding cost only. The purchase cost of the goods is displayed separately because it does not change with order size, so including it would hide the difference the order quantity actually makes.',
  },
  {
    value: 'worked-example',
    question: 'What does a typical MOQ premium look like?',
    answer:
      'Take a $10 unit, 10,000 units a year, $250 to place and receive one order, and 25% holding cost. EOQ is 1,414 units. If the supplier minimum is 5,000, you order less often but hold much more stock. Ordering-plus-holding cost rises from about $3,536 a year at the EOQ to $6,750 at the MOQ — roughly $3,200 a year for the privilege of the minimum. Volume discounts can offset that; the calculator does not guess them.',
  },
] as const;

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Free MOQ Calculator: EOQ vs Factory Minimum Cost',
    alternateName: 'EOQ and Economic Order Quantity Calculator',
    description:
      'Free MOQ calculator for Shopify and FBA importers. Enter demand, unit cost, and factory MOQ. See EOQ and the annual premium of ordering more than you need.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'MOQ and EOQ Calculator',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    inLanguage: 'en',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    creator: { '@type': 'Organization', name: siteName, url: baseUrl },
    featureList: [
      'Calculate economic order quantity',
      'Compare EOQ against supplier MOQ',
      'Price the annual MOQ premium',
      'Estimate annual holding and ordering cost',
      'Model cash tied up in inventory',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'MOQ Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function formatCurrency(value: number | null | undefined, currency: string) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '--';
  }

  return formatMoney({ currency, value });
}

export default function MOQCalculatorPage() {
  const [formData, setFormData] = useState<FormData>({
    annualDemand: '',
    holdingCostPct: '25',
    orderingCost: '',
    supplierMoq: '',
    unitCost: '',
  });
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [results, setResults] = useState<MoqResult | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [resultsStatus, setResultsStatus] = useState<
    'idle' | 'updated' | 'stale'
  >('idle');

  const startFreePath = pathsConfig.auth.startFree;
  const hasResults = results !== null;
  const hasSupplierMoq = parseNumericInput(formData.supplierMoq) > 0;

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setFormData((current) => ({
      ...current,
      [key]: value,
    }));

    setInputError(null);

    if (results) {
      setResultsStatus('stale');
    }
  }

  function calculate() {
    const annualDemand = parseNumericInput(formData.annualDemand);
    const holdingCostPercent = parseNumericInput(formData.holdingCostPct);
    const orderingCost = parseNumericInput(formData.orderingCost);
    const unitCost = parseNumericInput(formData.unitCost);
    const error = getMoqInputError({
      annualDemand,
      holdingCostPercent,
      orderingCost,
      unitCost,
    });

    if (error) {
      setInputError(error);
      setResults(null);
      setResultsStatus('idle');
      return;
    }

    setInputError(null);
    setResults(
      calculateMoq({
        annualDemand,
        holdingCostPercent,
        orderingCost,
        supplierMoq: formData.supplierMoq
          ? parseNumericInput(formData.supplierMoq)
          : null,
        unitCost,
      }),
    );
    setResultsStatus('updated');
  }

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border bg-muted relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.12*100%),transparent),_transparent_42%)]" />
          <div className="bg-primary/10 absolute -top-28 right-0 h-72 w-72 rounded-full blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="flex flex-col gap-6 pt-2 lg:pt-10">
                <div className="bg-primary/10 text-primary inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <Package className="h-4 w-4" />
                  Inventory Planning Tool
                </div>

                <div className="space-y-5">
                  <h1 className="font-heading text-foreground max-w-2xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                    <span className="text-primary">MOQ Calculator</span>: EOQ
                    vs Factory Minimum Cost
                  </h1>

                  <p className="text-muted-foreground max-w-xl text-lg leading-8">
                    Free tool for Shopify, Amazon FBA, and importers. Find the
                    economic order quantity, compare it to the factory MOQ, and
                    see the annual premium of ordering more than you need before
                    the next PO goes out.
                  </p>
                </div>

                <div className="flex flex-wrap gap-5 pt-2">
                  {heroHighlights.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-5 w-5" />
                      <span className="text-muted-foreground text-sm font-medium">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-border bg-card rounded-[10px] border p-6 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)] sm:p-8">
                <div className="mb-6 flex items-center gap-3">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-xl">
                    <Package className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-foreground text-xl font-bold">
                      Calculate EOQ and MOQ Impact
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Order quantity, cost balance, and MOQ premium
                    </p>
                  </div>
                </div>

                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-5"
                >
                  <CurrencyPicker onChange={setCurrency} value={currency} />

                  <div className="space-y-2">
                    <Label
                      htmlFor="unitCost"
                      className="text-muted-foreground text-sm font-bold"
                    >
                      Unit Cost ({currency})
                    </Label>
                    <Input
                      id="unitCost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unitCost}
                      onChange={(e) => updateField('unitCost', e.target.value)}
                      placeholder="10.00"
                      className="border-border bg-muted h-12 rounded-xl shadow-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="annualDemand"
                      className="text-muted-foreground text-sm font-bold"
                    >
                      Annual Demand (units)
                    </Label>
                    <Input
                      id="annualDemand"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.annualDemand}
                      onChange={(e) =>
                        updateField('annualDemand', e.target.value)
                      }
                      placeholder="10000"
                      className="border-border bg-muted h-12 rounded-xl shadow-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="orderingCost"
                      className="text-muted-foreground text-sm font-bold"
                    >
                      Cost Per Order ({currency})
                    </Label>
                    <Input
                      id="orderingCost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.orderingCost}
                      onChange={(e) =>
                        updateField('orderingCost', e.target.value)
                      }
                      placeholder="500"
                      className="border-border bg-muted h-12 rounded-xl shadow-none"
                    />
                    <p className="text-muted-foreground text-xs leading-5">
                      Freight coordination, customs, inspection, and admin, not
                      the price of the goods.
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label
                        htmlFor="holdingCostPct"
                        className="text-muted-foreground text-sm font-bold"
                      >
                        Holding Cost (% / year)
                      </Label>
                      <Input
                        id="holdingCostPct"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={formData.holdingCostPct}
                        onChange={(e) =>
                          updateField('holdingCostPct', e.target.value)
                        }
                        placeholder="25"
                        className="border-border bg-muted h-12 rounded-xl shadow-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="supplierMoq"
                        className="text-muted-foreground text-sm font-bold"
                      >
                        Supplier MOQ{' '}
                        <span className="font-medium">(optional)</span>
                      </Label>
                      <Input
                        id="supplierMoq"
                        type="number"
                        min="0"
                        step="1"
                        value={formData.supplierMoq}
                        onChange={(e) =>
                          updateField('supplierMoq', e.target.value)
                        }
                        placeholder="5000"
                        className="border-border bg-muted h-12 rounded-xl shadow-none"
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={calculate}
                    className="bg-primary shadow-primary/20 hover:bg-accent-deep/90 text-primary-foreground h-12 w-full rounded-xl text-base font-bold shadow-lg"
                  >
                    Calculate Optimal Order Quantity
                  </Button>

                  {inputError ? (
                    <div
                      aria-live="polite"
                      className="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>{inputError}</p>
                    </div>
                  ) : null}

                  {resultsStatus === 'updated' ? (
                    <div
                      aria-live="polite"
                      className="border-sage-fg/40 bg-sage-bg text-sage-fg flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
                    >
                      <CheckCircle className="text-sage-fg mt-0.5 h-4 w-4 shrink-0" />
                      <p>
                        Analysis updated below. Scroll down for the EOQ and the
                        MOQ premium.
                      </p>
                    </div>
                  ) : null}

                  {resultsStatus === 'stale' ? (
                    <div
                      aria-live="polite"
                      className="border-warn-fg/40 bg-warn-bg text-warn-fg flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
                    >
                      <AlertTriangle className="text-warn-fg mt-0.5 h-4 w-4 shrink-0" />
                      <p>
                        Inputs changed. Click calculate again to refresh the
                        results below.
                      </p>
                    </div>
                  ) : null}

                  <p className="text-muted-foreground text-xs leading-5">
                    EOQ is a planning model. Final order decisions should still
                    account for lead time, seasonality, cash flow, and supplier
                    risk.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="border-border bg-card border-y py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="border-primary/10 bg-primary/[0.06] rounded-[10px] border p-6 sm:p-8">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-bold tracking-[0.22em] uppercase">
                  EOQ Results
                </p>
                <h2 className="font-heading text-foreground text-2xl font-bold">
                  {hasResults
                    ? 'Economic order quantity analysis'
                    : 'Run a calculation to see the optimal order size'}
                </h2>
              </div>

              <div className="mt-8 space-y-6">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
                  <div>
                    <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                      EOQ
                    </p>
                    <p className="text-primary text-3xl font-black sm:text-4xl">
                      {hasResults ? results.eoq.toLocaleString('en-US') : '--'}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs leading-5">
                      The economic optimum
                    </p>
                  </div>

                  <div className="border-border border-l pl-0 sm:pl-6">
                    <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                      Recommended Order
                    </p>
                    <p className="text-foreground text-3xl font-black sm:text-4xl">
                      {hasResults
                        ? results.recommendedOrderQty.toLocaleString('en-US')
                        : '--'}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs leading-5">
                      {hasResults && !results.meetsMoq
                        ? 'Raised to meet the supplier minimum'
                        : 'The EOQ clears any minimum you entered'}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Orders / Year
                    </p>
                    <p className="text-foreground mt-2 text-lg font-bold">
                      {hasResults ? results.ordersPerYear : '--'}
                    </p>
                  </div>

                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Order Cycle
                    </p>
                    <p className="text-foreground mt-2 text-lg font-bold">
                      {hasResults ? `${results.orderCycleDays} days` : '--'}
                    </p>
                  </div>

                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Cash In Stock
                    </p>
                    <p className="text-foreground mt-2 text-lg font-bold">
                      {hasResults
                        ? formatCurrency(
                            results.averageInventoryValue,
                            currency,
                          )
                        : '--'}
                    </p>
                    <p className="text-muted-foreground mt-2 text-xs leading-5">
                      Average inventory value at this order size.
                    </p>
                  </div>
                </div>

                {hasSupplierMoq ? (
                  <div
                    className={`rounded-2xl border p-5 ${
                      hasResults && results.meetsMoq
                        ? 'border-sage-fg/40 bg-sage-bg'
                        : 'border-warn-fg/40 bg-warn-bg'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {hasResults && results.meetsMoq ? (
                        <CheckCircle className="text-sage-fg mt-0.5 h-5 w-5 shrink-0" />
                      ) : (
                        <RefreshCw className="text-warn-fg mt-0.5 h-5 w-5 shrink-0" />
                      )}
                      <div>
                        <p className="text-foreground text-sm font-semibold">
                          {hasResults && results.meetsMoq
                            ? 'Your EOQ already meets the supplier MOQ'
                            : 'The supplier MOQ is pushing you above the economic optimum'}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm leading-7">
                          {!hasResults
                            ? 'Add a supplier MOQ to compare the recommended quantity against the supplier minimum.'
                            : results.meetsMoq
                              ? 'You can order at the EOQ without taking on an MOQ penalty.'
                              : `Meeting this minimum costs about ${formatCurrency(
                                  results.moqPremium,
                                  currency,
                                )} a year, ${results.moqPremiumPercent}% more than ordering at the EOQ. That is the number to take into your next supplier negotiation.`}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="border-border bg-card/70 rounded-2xl border p-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.14em] uppercase">
                        Annual Ordering Cost
                      </p>
                      <p className="text-foreground mt-2 text-xl font-bold">
                        {hasResults
                          ? formatCurrency(results.annualOrderingCost, currency)
                          : '--'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.14em] uppercase">
                        Annual Holding Cost
                      </p>
                      <p className="text-foreground mt-2 text-xl font-bold">
                        {hasResults
                          ? formatCurrency(results.annualHoldingCost, currency)
                          : '--'}
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-4 grid gap-4 border-t pt-4 sm:grid-cols-2">
                    <div>
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.14em] uppercase">
                        Total Annual Inventory Cost
                      </p>
                      <p className="text-foreground mt-2 text-xl font-bold">
                        {hasResults
                          ? formatCurrency(results.totalAnnualCost, currency)
                          : '--'}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs leading-5">
                        Ordering + holding. Excludes the goods themselves.
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.14em] uppercase">
                        Annual Purchase Cost
                      </p>
                      <p className="text-foreground mt-2 text-xl font-bold">
                        {hasResults
                          ? formatCurrency(results.annualPurchaseCost, currency)
                          : '--'}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs leading-5">
                        The goods. Unchanged by order size, shown for context.
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-4 border-t pt-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.14em] uppercase">
                      Inventory Cost Per Unit
                    </p>
                    <p className="text-foreground mt-2 text-lg font-bold">
                      {hasResults
                        ? formatCurrency(results.inventoryCostPerUnit, currency)
                        : '--'}
                    </p>
                  </div>
                </div>

                {hasResults && results.warnings.length > 0 ? (
                  <ul className="border-warn-fg/40 bg-warn-bg text-warn-fg space-y-3 rounded-2xl border p-5 text-sm">
                    {results.warnings.map((warning) => (
                      <li key={warning} className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="leading-6">{warning}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              {hasResults ? (
                <div className="border-primary/20 bg-card mt-8 rounded-[8px] border p-6 shadow-sm sm:p-8">
                  <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
                    <div>
                      <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                        Go beyond a one-off calculation
                      </p>
                      <h3 className="font-heading text-foreground mt-2 text-2xl font-bold">
                        Your EOQ moves every time a cost does
                      </h3>
                      <p className="text-muted-foreground mt-3 max-w-3xl text-sm leading-7">
                        Freight, duty, and unit prices all shift between orders,
                        and each one changes the answer above. {siteName} keeps
                        supplier pricing, purchase orders, and real landed costs
                        in one workspace so your order quantities stay based on
                        what you actually pay today.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                      <Button asChild className="h-11 rounded-xl font-bold">
                        <Link href={startFreePath}>
                          Start Your 14-Day Trial
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="h-11 rounded-xl font-bold"
                      >
                        <Link href="/#features">See How It Works</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                How EOQ and MOQ Analysis Works
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                The model balances the cost of placing orders against the cost
                of carrying inventory between them.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {optimizationSteps.map((step) => {
                const Icon = step.icon;

                return (
                  <div
                    key={step.title}
                    className="border-border bg-card rounded-[8px] border p-8 shadow-sm"
                  >
                    <div className="bg-primary/10 mb-6 flex h-12 w-12 items-center justify-center rounded-xl">
                      <Icon className="text-primary h-5 w-5" />
                    </div>
                    <h3 className="text-foreground text-xl font-bold">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground mt-3 text-sm leading-7">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-border bg-card border-y py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                The Formulas This Calculator Uses
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                Nothing here is hidden. These are the exact calculations behind
                every number above.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2">
              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Economic order quantity
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  √((2 × annual demand × cost per order) ÷ holding cost per
                  unit)
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  Holding cost per unit is your unit cost multiplied by the
                  annual holding percentage.
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Total annual inventory cost
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  (demand ÷ order qty × cost per order) + (order qty ÷ 2 ×
                  holding cost)
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  At the EOQ these two halves are equal. That is the property
                  that defines it.
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  MOQ premium
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  total cost at the MOQ − total cost at the EOQ
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  The full cost gap, not just the extra holding cost. Ordering
                  less often saves ordering cost, and that offset belongs in the
                  number.
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  What sits outside the model
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  EOQ assumes steady demand and a fixed unit cost. Volume
                  discounts, lead-time risk, and freight breakpoints are not in
                  it, so check those with the{' '}
                  <Link
                    className="text-primary font-semibold hover:underline"
                    href="/tools/lead-time-calculator"
                  >
                    lead time calculator
                  </Link>{' '}
                  and the{' '}
                  <Link
                    className="text-primary font-semibold hover:underline"
                    href="/tools/landed-cost-calculator"
                  >
                    landed cost calculator
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="border-border relative overflow-hidden rounded-[10px] border bg-[radial-gradient(circle_at_center,var(--primary)_0%,var(--primary)_34%,var(--primary)_100%)] p-10 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)]">
                <div className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.16)_1px,transparent_1px)] [background-size:18px_18px] opacity-20" />
                <div className="relative flex aspect-[4/3] items-center justify-center">
                  <div className="absolute h-72 w-72 rounded-full border border-white/10" />
                  <div className="absolute h-56 w-56 rounded-full border border-white/15" />
                  <div className="absolute h-40 w-40 rounded-full border border-white/20" />
                  <div className="bg-card/10 flex h-28 w-28 items-center justify-center rounded-full border border-white/20 shadow-2xl backdrop-blur-sm">
                    <Network className="text-primary-foreground h-12 w-12" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                  Why MOQ Analysis Matters
                </h2>

                <div className="mt-8 space-y-6">
                  {whyItMatters.map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="bg-sage-fg/10 text-sage-fg mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-foreground text-lg font-bold">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground mt-1 text-sm leading-7">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <BarChart3 className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Demand planning
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Match order rhythm to actual usage
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <TrendingDown className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Cost control
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          See the inventory penalty clearly
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-foreground text-center text-3xl font-bold sm:text-4xl">
              MOQ and EOQ Calculator FAQ
            </h2>

            <Accordion type="single" collapsible className="mt-12 space-y-4">
              {faqItems.map((faq) => (
                <AccordionItem
                  key={faq.value}
                  value={faq.value}
                  className="border-border rounded-[8px] border px-6"
                >
                  <AccordionTrigger className="text-foreground text-left text-base font-bold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-16">
              <h2 className="text-foreground text-2xl font-bold">
                Related reading
              </h2>
              <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
                <li>
                  <Link
                    href="/tools/lead-time-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    Lead time calculator
                  </Link>{' '}
                  — a cheaper unit cost from a larger MOQ still has to survive
                  the days the stock sits.
                </li>
                <li>
                  <Link
                    href="/tools/landed-cost-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    Landed cost calculator
                  </Link>{' '}
                  — freight breakpoints change the real unit cost the EOQ is
                  using.
                </li>
                <li>
                  <Link
                    href="/blog/how-to-calculate-landed-cost"
                    className="text-primary font-semibold hover:underline"
                  >
                    How to calculate landed cost
                  </Link>{' '}
                  — put the MOQ decision on the same unit-cost basis as the rest
                  of the PO.
                </li>
              </ul>
            </div>

            <RelatedTools currentPath="/tools/moq-calculator" />
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The calculator is free. The inputs are the hard part.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                An EOQ is only as good as the costs behind it.
              </h2>
              <p className="text-background/70 mt-6 text-lg leading-8">
                Unit cost, freight, duty, and prep all move between orders, and
                every one of them shifts the answer above. {siteName} keeps
                purchase orders, supplier pricing, and real landed costs in one
                workspace, so the next time you size an order you are working
                from what you actually paid.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
                >
                  <Link href="/#features">See {siteName}</Link>
                </Button>
              </div>

              <div className="text-background/60 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {siteName} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{siteName}</p>
                      <p className="text-muted-foreground text-xs">
                        Example order sizing
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Costs current
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        SKU CB-1180 · supplier MOQ 5,000
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        $2,250
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Annual cost of meeting the minimum
                      </p>
                    </div>
                    <div className="rounded-full bg-amber-100 px-4 py-2 text-sm font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                      45% over EOQ
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[40%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>EOQ 2,000</span>
                    <span>MOQ 5,000</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <Package className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">2,000</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Economic order quantity
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <DollarSign className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$25,000</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Cash tied up at the MOQ
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <TrendingDown className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$0.73</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Inventory cost per unit
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Take the premium into the negotiation instead of absorbing
                      it quietly.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>
      </div>
    </>
  );
}
